/**
 * Analytics Services for PromptNote Web
 *
 * Services:
 * - PostHog: Product analytics with cross-domain tracking from promptnote-site
 * - GA4: Marketing attribution
 * - Hotjar: Session replay & heatmaps
 *
 * User Identification:
 * - Call identifyUser() after successful login
 * - Call clearUserIdentity() on logout
 */

import Hotjar from '@hotjar/browser';
import posthog from 'posthog-js';

// Configuration
const POSTHOG_KEY = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;
const POSTHOG_HOST = import.meta.env.VITE_PUBLIC_POSTHOG_HOST || 'https://eu.i.posthog.com';
const GA_MEASUREMENT_ID = 'G-BCFQL243V2'; // promptnote-web stream
const HOTJAR_SITE_ID = 6657110; // promptnote-web
const HOTJAR_VERSION = 6;

// Initialization flags
let posthogInitialized = false;
let gaInitialized = false;
let hotjarInitialized = false;

// ============================================
// Initialization Functions
// ============================================

/**
 * Initialize PostHog product analytics
 */
export function initPostHog() {
  if (typeof window === 'undefined' || posthogInitialized) return;

  if (!POSTHOG_KEY) {
    // console.warn('[PostHog] Missing API key');
    return;
  }

  try {
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      person_profiles: 'identified_only', // Only create profiles for identified users
      capture_pageview: true,
      capture_pageleave: true,
      autocapture: true,
    });
    posthogInitialized = true;
    // console.log('[PostHog] Initialized successfully');
  } catch (error) {
    console.error('[PostHog] Initialization failed:', error);
  }
}

/**
 * Initialize Google Analytics 4
 */
export function initGA4() {
  if (typeof window === 'undefined' || gaInitialized) return;

  try {
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(script);

    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
    window.gtag('js', new Date());
    window.gtag('config', GA_MEASUREMENT_ID, {
      send_page_view: true,
      anonymize_ip: true,
      allow_google_signals: true,
      allow_ad_personalization_signals: false,
    });

    gaInitialized = true;
    // console.log('[GA4] Initialized successfully');
  } catch (error) {
    console.error('[GA4] Initialization failed:', error);
  }
}

/**
 * Initialize Hotjar session recording
 */
export function initHotjar() {
  if (typeof window === 'undefined' || hotjarInitialized) return;

  try {
    Hotjar.init(HOTJAR_SITE_ID, HOTJAR_VERSION);
    hotjarInitialized = true;
    // console.log('[Hotjar] Initialized successfully');
  } catch (error) {
    console.error('[Hotjar] Initialization failed:', error);
  }
}

// ============================================
// User Identification
// ============================================

/**
 * Identify user across all analytics services
 * Call this after successful login/registration
 * @param {string} userId - Unique user ID
 * @param {object} userData - User properties (username, email, etc.)
 */
export function identifyUser(userId, userData = {}) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    // Google Analytics 4
    if (gaInitialized && window.gtag) {
      window.gtag('set', 'user_properties', {
        user_id: userId,
        username: userData.username,
      });
      // console.log('[GA4] User identified:', userId);
    }

    // PostHog
    if (posthogInitialized) {
      posthog.identify(userId, {
        username: userData.username,
        email: userData.email,
      });
      // console.log('[PostHog] User identified:', userId);
    }

    // Hotjar
    if (hotjarInitialized && window.hj) {
      window.hj('identify', userId, {
        username: userData.username,
        email: userData.email,
      });
      // console.log('[Hotjar] User identified:', userId);
    }
  } catch (error) {
    console.error('[Analytics] User identification failed:', error);
  }
}

/**
 * Clear user identity on logout
 * Resets analytics to anonymous tracking
 */
export function clearUserIdentity() {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    // PostHog - reset to anonymous
    if (posthogInitialized) {
      posthog.reset();
      // console.log('[PostHog] User identity cleared');
    }

    // Hotjar - no explicit reset API
    // GA4 - no explicit reset API

    // console.log('[Analytics] User identity cleared');
  } catch (error) {
    console.error('[Analytics] Failed to clear user identity:', error);
  }
}

// ============================================
// Event Tracking
// ============================================

/**
 * Track custom event across all analytics platforms
 * @param {string} eventName - Name of the event
 * @param {object} properties - Event properties
 */
export function trackEvent(eventName, properties = {}) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    // GA4
    if (gaInitialized && window.gtag) {
      window.gtag('event', eventName, properties);
    }

    // PostHog
    if (posthogInitialized) {
      posthog.capture(eventName, properties);
    }
  } catch (error) {
    console.error('[Analytics] Failed to track event:', error);
  }
}

/**
 * Track SPA route changes
 * Call this on React Router navigation
 * @param {string} url - New URL path
 */
export function trackPageView(url) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    // Hotjar's stateChange API for SPA navigation
    if (hotjarInitialized && window.hj) {
      window.hj('stateChange', url);
    }
  } catch (error) {
    console.error('[Hotjar] Page view tracking failed:', error);
  }
}

// ============================================
// Cross-Domain Tracking
// ============================================

/**
 * Capture cross-domain tracking parameters from URL hash
 * Call this on app initialization to continue tracking from promptnote-site
 */
export function captureCrossDomainParams() {
  if (!posthogInitialized || typeof window === 'undefined') {
    return;
  }

  try {
    const hash = window.location.hash;
    if (!hash) return;

    const params = new URLSearchParams(hash.substring(1));
    const distinctId = params.get('ph_distinct_id');
    const sessionId = params.get('ph_session_id');

    if (distinctId && sessionId) {
      // Set the distinct_id and session_id from landing page
      posthog.identify(distinctId);
      posthog.set_config({ session_id: sessionId });

      // console.log('[PostHog] Cross-domain tracking captured:', { distinctId, sessionId });

      // Clean up URL hash
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  } catch (error) {
    console.error('[PostHog] Failed to capture cross-domain params:', error);
  }
}

// ============================================
// Utility Functions
// ============================================

/**
 * Update super properties (sent with every event)
 * Use this for frequently changing context
 * @param {object} properties - Properties to register
 */
export function updateSuperProperties(properties) {
  if (typeof window === 'undefined' || !posthogInitialized) {
    return;
  }

  try {
    posthog.register(properties);
    // console.log('[PostHog] Super properties updated:', properties);
  } catch (error) {
    console.error('[PostHog] Failed to update super properties:', error);
  }
}

/**
 * Update user properties
 * @param {object} properties - Properties to set
 */
export function updateUserProperties(properties) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    // PostHog - update person properties
    if (posthogInitialized) {
      posthog.setPersonProperties(properties);
      // console.log('[PostHog] Person properties updated:', properties);
    }
  } catch (error) {
    console.error('[Analytics] Failed to update user properties:', error);
  }
}

/**
 * Set user properties only if not already set (for first-time events)
 * @param {object} properties - Properties to set once
 */
export function setOnceUserProperties(properties) {
  if (typeof window === 'undefined' || !posthogInitialized) {
    return;
  }

  try {
    posthog.setPersonPropertiesForFlags(properties, false);
    posthog.capture('$set', {
      $set_once: properties,
    });
    // console.log('[PostHog] Person properties set once:', properties);
  } catch (error) {
    console.error('[Analytics] Failed to set once user properties:', error);
  }
}

/**
 * Increment user properties (for counters like total_notes_created)
 * Uses localStorage to track counts client-side since PostHog JS SDK doesn't have native increment
 * @param {object} properties - Properties to increment { property: amount }
 */
export function incrementUserProperties(properties) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    if (posthogInitialized) {
      const STORAGE_KEY = 'promptnote_user_counters';
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');

      const updated = { ...stored };
      Object.entries(properties).forEach(([key, amount]) => {
        updated[key] = (updated[key] || 0) + amount;
      });

      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      posthog.setPersonProperties(updated);
    }
  } catch (error) {
    console.error('[Analytics] Failed to increment user properties:', error);
  }
}

/**
 * Get current user property counters from localStorage
 * @returns {object} Current counter values
 */
export function getUserCounters() {
  if (typeof window === 'undefined') {
    return {};
  }

  try {
    const STORAGE_KEY = 'promptnote_user_counters';
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
}

/**
 * Track session start and return events (D1, D7)
 * Call after user is confirmed authenticated
 * @param {object} user - User object with id, created_at
 */
export function trackSessionStart(user) {
  if (typeof window === 'undefined' || !posthogInitialized) {
    return;
  }

  try {
    const LAST_SESSION_KEY = 'promptnote_last_session_at';
    const SESSION_NUMBER_KEY = 'promptnote_session_number';
    const now = new Date();
    const lastSessionStr = localStorage.getItem(LAST_SESSION_KEY);
    let daysSinceLastSession = 0;
    let sessionNumber = 1;

    if (lastSessionStr) {
      const lastSession = new Date(lastSessionStr);
      daysSinceLastSession = Math.floor((now - lastSession) / (1000 * 60 * 60 * 24));
      sessionNumber = parseInt(localStorage.getItem(SESSION_NUMBER_KEY) || '1', 10) + 1;
    }

    localStorage.setItem(LAST_SESSION_KEY, now.toISOString());
    localStorage.setItem(SESSION_NUMBER_KEY, String(sessionNumber));

    trackEvent('session_started', {
      days_since_last_session: daysSinceLastSession,
      session_number: sessionNumber,
    });

    updateUserProperties({
      last_session_at: now.toISOString(),
      session_number: sessionNumber,
    });

    // Calculate days since signup for retention tracking
    if (user?.created_at) {
      const signupDateObj = new Date(user.created_at);
      const daysSinceSignup = Math.floor((now - signupDateObj) / (1000 * 60 * 60 * 24));
      updateUserProperties({ days_since_signup: daysSinceSignup });

      // D1 return (1 day since signup)
      if (daysSinceSignup >= 1 && daysSinceSignup < 2) {
        setOnceUserProperties({ returned_d1_at: now.toISOString() });
        trackEvent('user_returned_d1', {});
      }
      // D7 return (7+ days since signup)
      if (daysSinceSignup >= 7 && daysSinceSignup < 8) {
        setOnceUserProperties({ returned_d7_at: now.toISOString() });
        trackEvent('user_returned_d7', {});
      }
    }
  } catch (error) {
    console.error('[Analytics] Failed to track session start:', error);
  }
}

/**
 * Get PostHog instance for advanced usage
 * @returns {object|null} PostHog instance
 */
export function getPostHog() {
  return posthogInitialized ? posthog : null;
}
